import { useEffect, useMemo, useRef, useState } from "react";
import { useRoute } from "wouter";
import { ArrowLeft, Pause, Play, Send, Upload, Check, X, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { Segmented } from "@/components/layout/segmented";
import { StatusBadge } from "@/components/data/status-badge";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { BolhaWhatsApp, ConversaWhatsApp, SeloEntrega, SeloPublico } from "@/components/avisos/avisos-ui";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { cn } from "@/lib/utils";
import {
  useModeloAviso, usePausarAviso, usePublicarAviso, useRestaurarAviso, useTesteAviso, type ErroPublicar,
} from "@/hooks/use-avisos";
import {
  LIMITE_CARACTERES_AVISO,
  RODAPE_AVISO,
  amostraComCampoVazio,
  renderizarAviso,
  validarTextoAviso,
  verificarTextoAviso,
  type VariavelAviso,
} from "@shared/avisos";
import type { ModeloDetalheDTO } from "@shared/avisos-admin";

// Plataforma › Mensagens WhatsApp › Editar modelo.
//
// O editor NÃO deixa publicar texto quebrado: as verificações rodam ao vivo com
// a MESMA função do servidor (shared/avisos.ts), e o servidor repete a checagem
// (422). Cada publicação vira versão; "Restaurar" cria uma versão nova com o
// texto antigo — nunca reescreve histórico.

const PADRAO = { visao: "um" };
const PERMITIDOS = { visao: ["um", "grupo", "vazio"] } as const;

function fmtQuando(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function PlataformaMensagemEditar() {
  const [, params] = useRoute("/plataforma/mensagens/:chave");
  const chave = params?.chave;
  const consulta = useModeloAviso(chave);
  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });
  if (aguardando) return <>{aguardando}</>;
  // `key`: trocar de modelo (ou publicar uma versão nova) recomeça o rascunho.
  return <Editor key={`${consulta.data!.chave}:${consulta.data!.ativo.versao ?? 0}`} m={consulta.data!} />;
}

function Editor({ m }: { m: ModeloDetalheDTO }) {
  const { estado, definir } = useEstadoNaUrl(PADRAO, PERMITIDOS);
  const { voltar } = useNavegacao();
  const { toast } = useToast();
  const publicar = usePublicarAviso(m.chave);
  const restaurar = useRestaurarAviso(m.chave);
  const pausar = usePausarAviso(m.chave);
  const teste = useTesteAviso(m.chave);
  const ref = useRef<HTMLTextAreaElement>(null);

  const agrupa = !!m.variaveisGrupo;
  const visao = estado.visao === "grupo" && !agrupa ? "um" : estado.visao;
  const [texto, setTexto] = useState(m.ativo.texto);
  const [textoGrupo, setTextoGrupo] = useState(m.ativo.textoGrupo ?? "");
  // A lista do servidor (422) — some assim que o texto muda.
  const [errosServidor, setErrosServidor] = useState<ErroPublicar["erros"]>(null);
  useEffect(() => setErrosServidor(null), [texto, textoGrupo]);

  const editandoGrupo = visao === "grupo";
  const atual = editandoGrupo ? textoGrupo : texto;
  const contrato: VariavelAviso[] = editandoGrupo ? (m.variaveisGrupo ?? []) : m.variaveis;
  const verificacoes = useMemo(() => verificarTextoAviso(atual, contrato), [atual, contrato]);

  const errosUm = validarTextoAviso(texto, m.variaveis);
  const errosGrupo = agrupa ? validarTextoAviso(textoGrupo, m.variaveisGrupo ?? []) : [];
  const mudou = texto !== m.ativo.texto || (agrupa && textoGrupo !== (m.ativo.textoGrupo ?? ""));
  const podePublicar = mudou && errosUm.length === 0 && errosGrupo.length === 0 && !publicar.isPending;

  const amostra = useMemo(() => {
    if (editandoGrupo) return { vars: m.amostras.grupo ?? m.amostras.um, vazia: null as string | null };
    if (visao === "vazio") return amostraComCampoVazio(m.variaveis, m.amostras.um, texto);
    return { vars: m.amostras.um, vazia: null as string | null };
  }, [editandoGrupo, visao, m, texto]);
  const previa = renderizarAviso(atual, amostra.vars);

  function inserir(nome: string) {
    const alvo = ref.current;
    const trecho = `{${nome}}`;
    const setter = editandoGrupo ? setTextoGrupo : setTexto;
    if (!alvo) { setter(atual + trecho); return; }
    const ini = alvo.selectionStart ?? atual.length;
    const fim = alvo.selectionEnd ?? atual.length;
    setter(atual.substring(0, ini) + trecho + atual.substring(fim));
    requestAnimationFrame(() => {
      alvo.focus();
      alvo.setSelectionRange(ini + trecho.length, ini + trecho.length);
    });
  }

  function aoPublicar() {
    publicar.mutate({ texto, textoGrupo: agrupa ? textoGrupo : null }, {
      onSuccess: r => toast({ title: `Versão ${r.versao} publicada`, description: "É o texto que sai a partir de agora." }),
      onError: e => {
        setErrosServidor(e.erros);
        toast({ title: "Não deu para publicar", description: e.mensagem, variant: "destructive" });
      },
    });
  }

  function aoTestar() {
    teste.mutate({ amostra: visao as "um" | "grupo" | "vazio", texto, textoGrupo: agrupa ? textoGrupo : null }, {
      onSuccess: r => toast({
        title: r.ok ? "Teste enviado" : "O teste não saiu",
        description: r.ok ? `Para ${r.destino}${r.sandbox ? " — sandbox (/dev/agente/inbox)" : ""}.` : r.erro,
        variant: r.ok ? undefined : "destructive",
      }),
      onError: e => toast({ title: "Não deu para testar", description: e.mensagem, variant: "destructive" }),
    });
  }

  function aoPausar() {
    pausar.mutate(!m.pausado, {
      onSuccess: r => toast({ title: r.pausado ? "Modelo pausado" : "Modelo retomado", description: r.pausado ? "Os avisos deste modelo deixam de sair." : "Os avisos deste modelo voltam a sair." }),
      onError: e => toast({ title: "Não deu certo", description: e.message, variant: "destructive" }),
    });
  }

  function aoRestaurar(versao: number) {
    restaurar.mutate(versao, {
      onSuccess: r => toast({ title: `Restaurado como versão ${r.versao}`, description: versao === 0 ? "O texto padrão do código voltou ao ar." : `O texto da versão ${versao} voltou ao ar.` }),
      onError: e => toast({ title: "Não deu para restaurar", description: e.message, variant: "destructive" }),
    });
  }

  const errosDoServidorAqui = errosServidor ? (editandoGrupo ? errosServidor.grupo : errosServidor.um) : [];

  return (
    <div className="space-y-5">
      <button type="button" onClick={() => voltar("/plataforma/mensagens")} className="inline-flex items-center gap-1 text-xs font-semibold text-primary" data-testid="link-voltar-modelos">
        <ArrowLeft className="h-3.5 w-3.5" />Modelos
      </button>
      <PageHeader
        titulo={m.nome}
        descricao={`${m.chave} · ${m.gatilho} · ${m.eventoRotulo}`}
        empilharQuandoEstreito
        acao={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={aoPausar} disabled={pausar.isPending} className={ALVO_TOQUE_BOTAO} data-testid="button-pausar">
              {m.pausado ? <><Play className="h-4 w-4 mr-2" />Retomar</> : <><Pause className="h-4 w-4 mr-2" />Pausar</>}
            </Button>
            <Button
              variant="outline" onClick={aoTestar} disabled={!m.teste.pode || teste.isPending} className={ALVO_TOQUE_BOTAO}
              title={m.teste.motivo ?? `Envia para ${m.teste.destino}${m.teste.sandbox ? " (sandbox)" : ""}`} data-testid="button-testar"
            >
              <Send className="h-4 w-4 mr-2" />Enviar teste para mim
            </Button>
            <Button onClick={aoPublicar} disabled={!podePublicar} className={ALVO_TOQUE_BOTAO} data-testid="button-publicar">
              <Upload className="h-4 w-4 mr-2" />Publicar versão {m.proximaVersao}
            </Button>
          </div>
        }
      />
      <div className="flex flex-wrap items-center gap-1.5">
        <SeloPublico publico={m.publico} /><SeloEntrega entrega={m.entrega} />
        {m.pausado ? <StatusBadge tom="neutro" ponto>Pausado</StatusBadge> : <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge>}
        {!m.teste.pode && <span className="text-xs text-muted-foreground">· {m.teste.motivo}</span>}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] items-start">
        <div className="space-y-4 min-w-0">
          <Segmented
            valor={visao}
            onChange={v => definir({ visao: v })}
            testId="seg-amostra"
            opcoes={[
              { id: "um", label: "Um aviso" },
              ...(agrupa ? [{ id: "grupo", label: "Agrupado" }] : []),
              { id: "vazio", label: "Campo vazio" },
            ]}
          />
          <div className="space-y-1.5">
            <label htmlFor="texto-aviso" className="text-xs font-semibold text-muted-foreground">
              {editandoGrupo ? "Texto quando agrupa (2 ou mais na janela)" : "Texto"}
            </label>
            <Textarea
              id="texto-aviso"
              ref={ref}
              value={atual}
              onChange={e => (editandoGrupo ? setTextoGrupo : setTexto)(e.target.value)}
              spellCheck={false}
              className="min-h-[260px] font-mono text-[12.5px] leading-relaxed"
              data-testid="textarea-aviso"
            />
            <p className="text-xs text-muted-foreground">
              {atual.length} de {LIMITE_CARACTERES_AVISO.toLocaleString("pt-BR")} caracteres · o rodapé “{RODAPE_AVISO}” é fixo e entra sozinho.
            </p>
          </div>

          <div className="rounded-lg border bg-card">
            <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
              <p className="text-sm font-semibold">Variáveis deste modelo</p>
              <p className="text-xs text-muted-foreground">toque para inserir · verde = obrigatória</p>
            </div>
            <div className="flex flex-wrap gap-1.5 p-3" data-testid="variaveis">
              {contrato.map(v => (
                <button
                  key={v.nome}
                  type="button"
                  onClick={() => inserir(v.nome)}
                  className={cn(
                    "rounded-md border bg-muted/50 px-2 py-1 font-mono text-[11.5px] hover-elevate",
                    v.obrigatoria ? "border-primary/60 text-primary" : "",
                  )}
                  data-testid={`var-${v.nome}`}
                >
                  {`{${v.nome}}`}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border bg-card" data-testid="verificacoes">
            <p className="border-b px-4 py-2.5 text-sm font-semibold">Verificações</p>
            <ul className="grid gap-1.5 p-3 text-[12.5px]">
              {verificacoes.map(v => (
                <li key={v.codigo} className="flex items-start gap-2" data-testid={`check-${v.codigo}`} data-ok={v.ok}>
                  {v.ok ? <Check className="h-4 w-4 shrink-0 text-sucesso" /> : <X className="h-4 w-4 shrink-0 text-destructive" />}
                  <span className={v.ok ? "" : "text-destructive"}>{v.rotulo}</span>
                </li>
              ))}
              {errosDoServidorAqui.map(e => (
                <li key={`srv-${e.codigo}`} className="flex items-start gap-2 text-destructive"><X className="h-4 w-4 shrink-0" />{e.rotulo}</li>
              ))}
            </ul>
            {(errosUm.length > 0 && editandoGrupo) || (errosGrupo.length > 0 && !editandoGrupo) ? (
              <p className="border-t px-4 py-2 text-xs text-destructive">
                O {editandoGrupo ? "texto de um aviso" : "texto de grupo"} também tem erro — o Publicar espera os dois.
              </p>
            ) : null}
          </div>
        </div>

        <div className="space-y-4 min-w-0 lg:sticky lg:top-4">
          <ConversaWhatsApp para={`prévia para ${m.publico === "COMPRADOR" ? "o comprador" : m.publico === "PRODUTOR" ? "o produtor" : "o outro lado"}`}>
            <BolhaWhatsApp texto={previa} testId="previa-aviso" />
          </ConversaWhatsApp>
          {amostra.vazia && (
            <div className="flex gap-2 rounded-lg border border-info/30 bg-info-suave p-3 text-[12.5px] text-info" data-testid="callout-vazio">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <p>Sem <span className="font-mono">{`{${amostra.vazia}}`}</span>, a linha inteira sai. É a mesma regra das mensagens do romaneio: nada de dado vazio no meio da frase.</p>
            </div>
          )}

          <div className="rounded-lg border bg-card" data-testid="versoes">
            <p className="border-b px-4 py-2.5 text-sm font-semibold">Versões</p>
            <ul className="divide-y text-[12.5px]">
              {m.versoes.map(v => (
                <li key={v.versao} className="grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-2 px-4 py-2">
                  <b>v{v.versao}</b>
                  <span className="truncate text-muted-foreground">{v.por ?? "—"} · {fmtQuando(v.em)}</span>
                  {v.ativa
                    ? <StatusBadge tom="sucesso">no ar</StatusBadge>
                    : <Button variant="ghost" size="sm" className="h-auto px-1.5 py-0.5 text-primary font-semibold" onClick={() => aoRestaurar(v.versao)} disabled={restaurar.isPending} data-testid={`button-restaurar-${v.versao}`}>Restaurar</Button>}
                </li>
              ))}
              <li className="grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-2 px-4 py-2">
                <b>{m.versoes.length === 0 ? "v0" : "—"}</b>
                <span className="text-muted-foreground">padrão do código</span>
                {m.ativo.versao === null
                  ? <StatusBadge tom="sucesso">no ar</StatusBadge>
                  : <Button variant="ghost" size="sm" className="h-auto px-1.5 py-0.5 text-primary font-semibold" onClick={() => aoRestaurar(0)} disabled={restaurar.isPending} data-testid="button-restaurar-padrao">Restaurar</Button>}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
